import ControlledTable from "@/components/controlled-table";
import UserRepository from "@/utilities/repositories/Users";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getColumns } from "./columns";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { IUserData, IUserDataResponse } from "@/utilities/types/Users";
import FilterElement, { ModuleType } from "./filter-element";
import toast from "react-hot-toast";
import AuthRepository from "@/utilities/repositories/Auths";
import { SignInResponse } from "@/utilities/types/Auths";
import { useAuthHelper } from "@/components/auth/helper";
import NoteModal from "./note-modal";
import SuspendModal from "@/components/suspend-website/SuspendModal";
import { useTableData } from "@/hooks/useTableData";
import ColorTagSelector from "@/components/color-tag/ColorTagSelector";
import ColorTagManager from "@/components/color-tag/ColorTagManager";
import ColorTagRepository from "@/utilities/repositories/ColorTag";
import { CreateColorTagDto, IColorTag } from "@/utilities/types/ColorTag";
import { COLOR_TAG_STYLES } from "@/config/constants";
import UpdateUserCreditModal from "@/app/app-shared/advanced-features/features/ad-credit/UpdateUserCreditModal";
import ChangePasswordModal from "./change-password-modal";
import DeleteUserModal from "./delete-user-modal";
import MonthlyLeadSummaryTable from "./monthly-lead-summary-table";

export default function UserTable() {
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [colorTagSelectorOpen, setColorTagSelectorOpen] = useState(false);
  const [colorTagManagerOpen, setColorTagManagerOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserData | null>(null);
  const [colorTags, setColorTags] = useState<IColorTag[]>([]);

  const { request: deleteUser } = useApi<IResponseData<IUserDataResponse>>({
    request: UserRepository.deleteUser,
    enableToast: true,
  });

  const { request: signInAgent } = useApi<IResponseData<SignInResponse>>({
    request: AuthRepository.adminSignInAgent,
    enableToast: true,
  });

  const { request: createColorTag } = useApi({
    request: ColorTagRepository.createColorTag,
    enableToast: true,
  });

  const { request: updateColorTag } = useApi({
    request: ColorTagRepository.updateColorTag,
    enableToast: true,
  });

  const { request: deleteColorTag } = useApi({
    request: ColorTagRepository.deleteColorTag,
    enableToast: true,
  });

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const { refreshAuth } = useAuthHelper();

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    handleReset,
    refresh,
    filters,
    updateFilter,
  } = useTableData({
    server: {
      request: UserRepository.getUsers,
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onUpdateUser = useCallback(
    async (id: string, status: "active" | "inactive") => {
      await UserRepository.updateUser(id, {
        statusAccount: status,
      });
      refresh(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [refresh],
  );

  const onEditNote = useCallback((userId: string, note?: string) => {
    setSelectedUser({ id: userId, note } as IUserData);
    setNoteModalOpen(true);
  }, []);

  const onOpenSuspendModal = useCallback((user: IUserData) => {
    setSelectedUser(user);
    setSuspendModalOpen(true);
  }, []);

  const onManageColorTags = useCallback((user: IUserData) => {
    setSelectedUser(user);
    setColorTagSelectorOpen(true);
  }, []);

  const onOpenCreditModal = useCallback((userId: string) => {
    setSelectedUser({ id: userId } as IUserData);
    setCreditModalOpen(true);
  }, []);

  const onChangePassword = useCallback((userId: string, userName?: string) => {
    setSelectedUser({ id: userId, firstName: userName } as IUserData);
    setChangePasswordModalOpen(true);
  }, []);

  const onOpenDeleteModal = useCallback((user: IUserData) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  }, []);

  const onDeleteUser = useCallback(async () => {
    if (selectedUser?.id) {
      await deleteUser(selectedUser.id);
      refresh();
    }
  }, [selectedUser, deleteUser, refresh]);

  const loadColorTags = useCallback(async () => {
    try {
      const response = await ColorTagRepository.getColorTags();
      if (response.success && response.data) {
        setColorTags(() => {
          const colorTags: IColorTag[] =
            response?.data?.items?.filter(
              (item: IColorTag) => COLOR_TAG_STYLES[item.name],
            ) || [];
          return colorTags;
        });
      }
    } catch (error) {
      console.error("Failed to load color tags:", error);
    }
  }, []);

  // Load color tags on component mount
  useEffect(() => {
    loadColorTags();
  }, [loadColorTags]);

  const onLoginAccountAgent = async (email: string) => {
    try {
      const response = await signInAgent({
        email,
      });
      if (response?.data) {
        refreshAuth(response?.data);
        window.location.reload();
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";
      toast.error(errorMessage);
    }
  };

  const handlePackageFilter = useCallback(
    (packageId?: string) => {
      updateFilter("packageIds", [packageId || ""]);
    },
    [updateFilter],
  );

  const handleModuleFilter = useCallback(
    (modules?: ModuleType[]) => {
      updateFilter("isLDP", undefined);
      updateFilter("isDirectory", undefined);
      if (modules?.includes("LDP")) {
        updateFilter("isLDP", "true");
      }
      if (modules?.includes("Directory")) {
        updateFilter("isDirectory", "true");
      }
    },
    [updateFilter],
  );

  const handlePromotionFilter = useCallback(
    (promotionId?: string) => {
      updateFilter("packagePromotionIds", [promotionId || ""]);
    },
    [updateFilter],
  );

  const handleCreateColorTag = useCallback(
    async (data: CreateColorTagDto) => {
      await createColorTag(data);
      await loadColorTags();
    },
    [createColorTag, loadColorTags],
  );

  const handleUpdateColorTag = useCallback(
    async (id: string, data: Partial<CreateColorTagDto>) => {
      await updateColorTag(id, data);
      await loadColorTags();
    },
    [updateColorTag, loadColorTags],
  );

  const handleDeleteColorTag = useCallback(
    async (id: string) => {
      await deleteColorTag(id);
      await loadColorTags();
    },
    [deleteColorTag, loadColorTags],
  );

  const selectedModules = useMemo(() => {
    const modules = [];
    if (filters?.isLDP === "true") modules.push("LDP");
    if (filters?.isDirectory === "true") modules.push("Directory");
    return modules;
  }, [filters?.isLDP, filters?.isDirectory]);

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onOpenDeleteModal,
        onLoginAccountAgent,
        handleReset,
        refresh,
        onEditNote,
        onOpenSuspendModal,
        onManageColorTags,
        onOpenCreditModal,
        onChangePassword,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onHeaderCellClick,
      sortConfig.key,
      sortConfig.direction,
      onOpenDeleteModal,
      tableData,
      onLoginAccountAgent,
      onUpdateUser,
      onEditNote,
      onOpenSuspendModal,
      onManageColorTags,
      onOpenCreditModal,
      onChangePassword,
    ],
  );

  return (
    <>
      <FilterElement
        onSearch={handleSearch}
        searchTerm={searchTerm}
        onPackageFilter={handlePackageFilter}
        selectedPackage={filters?.packageIds?.[0]}
        onModulesFilter={handleModuleFilter}
        selectedModules={selectedModules as ModuleType[]}
        onPromotionFilter={handlePromotionFilter}
        selectedPromotion={filters?.packagePromotionIds?.[0]}
        variant="users"
      />

      {selectedUser && (
        <NoteModal
          isOpen={noteModalOpen}
          onClose={() => setNoteModalOpen(false)}
          userId={selectedUser?.id}
          initialNote={selectedUser?.note}
          onSuccess={refresh}
        />
      )}

      <SuspendModal
        open={suspendModalOpen}
        onClose={() => setSuspendModalOpen(false)}
        user={selectedUser}
        userSuspend={selectedUser?.userSuspends?.[0] || null}
        refresh={refresh}
      />

      {selectedUser && (
        <ColorTagSelector
          isOpen={colorTagSelectorOpen}
          onClose={() => setColorTagSelectorOpen(false)}
          userId={selectedUser.id}
          userColorTags={selectedUser.userColorTags || []}
          availableColorTags={colorTags}
          refresh={refresh}
          onOpenColorTagManager={() => setColorTagManagerOpen(true)}
        />
      )}

      <ColorTagManager
        isOpen={colorTagManagerOpen}
        onClose={() => setColorTagManagerOpen(false)}
        colorTags={colorTags}
        onCreateTag={handleCreateColorTag}
        onUpdateTag={handleUpdateColorTag}
        onDeleteTag={handleDeleteColorTag}
      />

      {selectedUser && (
        <UpdateUserCreditModal
          isOpen={creditModalOpen}
          onClose={() => setCreditModalOpen(false)}
          userId={selectedUser.id}
          onSuccess={refresh}
        />
      )}

      {selectedUser && (
        <ChangePasswordModal
          isOpen={changePasswordModalOpen}
          onClose={() => setChangePasswordModalOpen(false)}
          userId={selectedUser.id}
          userName={`${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
          onSuccess={refresh}
        />
      )}

      {selectedUser && (
        <DeleteUserModal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={onDeleteUser}
          userName={`${selectedUser.firstName || ""} ${selectedUser.lastName || ""}`.trim()}
        />
      )}

      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-expect-error - The ControlledTable component expects a different column type
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />

      <MonthlyLeadSummaryTable />
    </>
  );
}
